import React, { useEffect, useState } from 'react';

import { Button, Stack, TextField, Typography } from '@mui/material';

import type { LensView } from './types';
import type { BoardEditor } from './useBoardEdit';

type ViewCodeProps = { id: string; view?: LensView; editor: BoardEditor };

const PARTS: Array<[keyof LensView, string]> = [
  ['html', 'HTML'],
  ['css', 'CSS'],
  ['js', 'Script'],
];

const sourceOf = (view?: LensView): LensView => ({ html: view?.html ?? '', css: view?.css ?? '', js: view?.js ?? '' });

export default function ViewCode({ id, view, editor }: ViewCodeProps) {
  const saved = sourceOf(view);
  const [draft, setDraft] = useState(saved);
  useEffect(() => setDraft(sourceOf(view)), [view]);
  const changed = PARTS.filter(([part]) => draft[part] !== saved[part]);
  return (
    <Stack data-lens-view-code={id} spacing={1.5}>
      {PARTS.map(([part, label]) => (
        <TextField
          fullWidth
          inputProps={{ spellCheck: false, sx: { fontFamily: 'monospace', fontSize: 12 } }}
          key={part}
          label={label}
          maxRows={18}
          minRows={part === 'html' ? 6 : 3}
          multiline
          onChange={(event) => setDraft({ ...draft, [part]: event.target.value })}
          value={draft[part]}
        />
      ))}
      <Stack alignItems="center" direction="row" spacing={1}>
        <Button
          disabled={editor.pending || !changed.length}
          onClick={() => editor.apply({ op: 'view', id, ...Object.fromEntries(changed.map(([part]) => [part, draft[part]])) })}
          size="small"
          variant="contained"
        >
          Save the code
        </Button>
        <Button disabled={!changed.length} onClick={() => setDraft(saved)} size="small" variant="text">
          Revert
        </Button>
      </Stack>
      <Typography color="text.secondary" variant="caption">
        The view reads its block’s rows as lens.data and draws in a sandbox; Undo takes a save back.
      </Typography>
    </Stack>
  );
}
